import { motion, AnimatePresence } from "framer-motion";
import { Check, Clock, Lock, Play, Sparkles } from "lucide-react";
import {
  SCALE_PATH_MISSIONS,
  isScalePathMissionUnlocked,
  type ScalePathMission,
} from "../../../lib/scales/scalePathPlan";
import type { ScalePathProgress } from "../../../lib/scales/scalePathProgress";
import { BEGINNER_PATH_THEME as PATH } from "../../../lib/beginnerPath/beginnerPathTheme";
import { BEGINNER_PATH_EXERCISE_GRID_CLASS } from "../../../lib/beginnerPath/beginnerPathLayout";

export interface ScalePathLibraryProps {
  progress: ScalePathProgress;
  pathMode?: boolean;
  onSelectMission: (missionId: string) => void;
}

export function ScalePathLibrary({
  progress,
  pathMode = false,
  onSelectMission,
}: ScalePathLibraryProps) {
  const completed = progress.completedMissionIds.length;
  const total = SCALE_PATH_MISSIONS.length;

  return (
    <div className={`w-full ${pathMode ? "pb-8" : "py-4"}`}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          {!pathMode && (
            <>
              <p className="text-xs tracking-widest uppercase text-[#c4b5fd]/70">
                Escalas · camino interactivo
              </p>
              <h2 className="mt-2 font-serif text-2xl font-light text-white/90">
                Misiones POS I
              </h2>
            </>
          )}
          <p className={`text-sm ${pathMode ? "" : "mt-2"} text-white/55`}>
            {completed}/{total} misiones · motor 3NPS (misma lógica que explorador libre)
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/45">
          <Sparkles className="h-4 w-4 text-[#c4b5fd]" />
          Aprender + Practicar con mic
        </div>
      </div>

      <div className={BEGINNER_PATH_EXERCISE_GRID_CLASS}>
        <AnimatePresence mode="popLayout">
          {SCALE_PATH_MISSIONS.map((mission) => (
            <MissionCard
              key={mission.id}
              mission={mission}
              completed={progress.completedMissionIds.includes(mission.id)}
              unlocked={isScalePathMissionUnlocked(mission, progress.completedMissionIds)}
              onOpen={() => onSelectMission(mission.id)}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function MissionCard({
  mission,
  completed,
  unlocked,
  onOpen,
}: {
  mission: ScalePathMission;
  completed: boolean;
  unlocked: boolean;
  onOpen: () => void;
}) {
  return (
    <motion.button
      type="button"
      layout
      disabled={!unlocked}
      onClick={onOpen}
      className="relative text-left rounded-2xl border p-5 transition-all disabled:opacity-45 disabled:cursor-not-allowed"
      style={{
        borderColor: completed ? PATH.cardBorderLavender : PATH.cardBorder,
        background: completed ? "rgba(196, 181, 253, 0.08)" : PATH.cardBg,
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <span
          className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold"
          style={{ background: "rgba(196, 181, 253, 0.15)", color: "#c4b5fd" }}
        >
          {mission.order}
        </span>
        {completed ? (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-400/90">
            <Check className="w-3.5 h-3.5" /> Hecho
          </span>
        ) : !unlocked ? (
          <Lock className="w-4 h-4 text-white/30" />
        ) : null}
      </div>
      <h3 className="font-serif font-light text-lg text-white/90 mb-1">{mission.title}</h3>
      <p className="text-sm text-white/55 mb-2">{mission.subtitle}</p>
      <p className="text-xs text-white/45 line-clamp-2 mb-3">{mission.description}</p>
      <div className="flex flex-wrap gap-1 mb-3">
        {mission.tags.map((tag) => (
          <span
            key={tag}
            className="text-[9px] px-1.5 py-0.5 rounded font-medium"
            style={{ background: "rgba(196, 181, 253, 0.12)", color: "#c4b5fd" }}
          >
            {tag}
          </span>
        ))}
      </div>
      <p className="text-[10px] flex items-center gap-1 text-white/35">
        <Clock className="w-3 h-3" />~{mission.timeEstimateMin} min
      </p>
      {unlocked && (
        <div className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-[#100f0e] px-3 py-2 rounded-xl"
          style={{ background: PATH.btnLavender }}
        >
          <Play className="w-3.5 h-3.5" fill="#100f0e" />
          Abrir misión
        </div>
      )}
    </motion.button>
  );
}

export default ScalePathLibrary;
